import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { expandEvent, nextOccurrence } from '../utils/recurrence';
import './EventsPage.calendar.css';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CalendarIcon,
} from '@heroicons/react/24/solid';
import {
  HeartIcon as HeartOutline,
  Squares2X2Icon,
  ListBulletIcon,
  UserGroupIcon,
  CakeIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon,
  SparklesIcon,
  AcademicCapIcon,
  CpuChipIcon,
  PaintBrushIcon,
  ArrowUpIcon,
  MagnifyingGlassIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { getCategoryColor } from '../utils/categoryColors';
import { createFormatEventDate } from '../utils/formatEventDate';

import FeaturedCarousel from '../components/events/FeaturedCarousel';
import DateStrip from '../components/events/DateStrip';
import SectionScroller from '../components/events/SectionScroller';
import EventCard from '../components/events/EventCard';
import EventModal from '../components/events/EventModal';
import CalendarTab from '../components/events/CalendarTab';
import SortToolbar from '../components/events/SortToolbar';

const ITEMS_PER_PAGE = 10;

const TIER_META = {
  General:    { icon: UserGroupIcon,         color: '#64748b' },
  Gastronomy: { icon: CakeIcon,              color: '#ef4444' },
  Shops:      { icon: ShoppingBagIcon,       color: '#3b82f6' },
  Services:   { icon: WrenchScrewdriverIcon, color: '#0f172a' },
  Health:     { icon: HeartOutline,          color: '#10b981' },
  Wellness:   { icon: SparklesIcon,          color: '#ec4899' },
  Education:  { icon: AcademicCapIcon,       color: '#f59e0b' },
  Technical:  { icon: CpuChipIcon,           color: '#6366f1' },
  Culture:    { icon: PaintBrushIcon,        color: '#8b5cf6' },
};

const isSameDay = (d1, d2) => {
  if (!d1 || !d2) return false;
  const a = new Date(d1);
  const b = new Date(d2);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const EventsPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentLang = i18n.language.split('-')[0];

  // --- States ---
  const [currentView, setCurrentView] = useState('month');
  const [events, setEvents] = useState([]);
  const [starEvents, setStarEvents] = useState([]);
  const [savedEventIds, setSavedEventIds] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [activeTab, setActiveTab] = useState('cards');
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const sentinelRef = useRef(null);

  const [description, setDescription] = useState('');
  const [serviceProvider, setServiceProvider] = useState('');

  const [calendarRange, setCalendarRange] = useState(() => {
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  });

  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [isCarouselAnimating, setIsCarouselAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(null);
  const prevIndexRef = useRef(0);
  const [calendarHeight, setCalendarHeight] = useState(window.innerWidth < 768 ? '420px' : '520px');
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [sortBy, setSortBy] = useState('dateAsc');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const parseDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  const normalizeKey = (str) => {
    if (!str) return '';
    return str.toString().toLowerCase().trim().replace(/\s+/g, '-');
  };

  const formatEventDate = createFormatEventDate(i18n.language);

  const fetchEvents = async () => {
    try {
      const res = await api.get('/events', { params: { description, serviceProvider } });
      const formatted = res.data.map(event => ({
        ...event,
        start: parseDate(event.start),
        end: parseDate(event.end),
      }));
      setEvents(formatted);
      setEventsLoaded(true);
      setIsLoading(false);
    } catch (err) {
      console.error("Error loading events", err);
      setEventsLoaded(true);
      setIsLoading(false);
    }
  };

  const fetchStarEvents = async () => {
    try {
      const res = await api.get('/events/star');
      const formatted = res.data.map(event => ({
        ...event,
        start: parseDate(event.start),
        end: parseDate(event.end),
      }));
      setStarEvents(formatted);
    } catch (err) {
      console.error("Error loading star events", err);
    }
  };

  const fetchUserSaved = async () => {
    if (!user) return;
    try {
      const res = await api.get('/user/favorites');
      const ids = (res.data.events || []).map(e => e._id);
      setSavedEventIds(ids);
    } catch (err) {
      console.error("Error loading saved events", err);
    }
  };

  const nextCarousel = useCallback(() => {
    if (isCarouselAnimating || starEvents.length === 0) return;
    prevIndexRef.current = currentCarouselIndex;
    setIsCarouselAnimating(true);
    setCurrentCarouselIndex((prev) => (prev + 1) % starEvents.length);
  }, [isCarouselAnimating, starEvents.length, currentCarouselIndex]);

  const prevCarousel = useCallback(() => {
    if (isCarouselAnimating || starEvents.length === 0) return;
    prevIndexRef.current = currentCarouselIndex;
    setIsCarouselAnimating(true);
    setCurrentCarouselIndex((prev) => (prev - 1 + starEvents.length) % starEvents.length);
  }, [isCarouselAnimating, starEvents.length, currentCarouselIndex]);

  useEffect(() => {
    fetchEvents();
    fetchStarEvents();
    fetchUserSaved();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link: auto-open event modal when ?event=<id> is in the URL
  useEffect(() => {
    if (!eventsLoaded) return;
    const eventId = searchParams.get('event');
    if (!eventId) return;
    const match = events.find(e => e._id === eventId);
    if (match) {
      setSelectedEvent(match);
      setIsDescExpanded(false);
      searchParams.delete('event');
      setSearchParams(searchParams, { replace: true });
    }
  }, [eventsLoaded, events, searchParams, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => {
      setCurrentView(window.innerWidth < 768 ? 'agenda' : 'month');
      setCalendarHeight(window.innerWidth < 480 ? '340px' : window.innerWidth < 768 ? '420px' : '520px');
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (starEvents.length > 0 && !isPaused) {
      const interval = setInterval(nextCarousel, 5000);
      return () => clearInterval(interval);
    }
  }, [starEvents.length, isPaused, nextCarousel]);

  useEffect(() => {
    const timer = setTimeout(() => setIsCarouselAnimating(false), 500);
    return () => clearTimeout(timer);
  }, [currentCarouselIndex]);

  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [selectedCategory, selectedTiers, description, serviceProvider, selectedDate]);

  // Back-to-top scroll listener
  useEffect(() => {
    let timeout;
    const handleScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setShowBackToTop(window.scrollY > 600);
      }, 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeout);
    };
  }, []);

  // Respect prefers-reduced-motion
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) setIsPaused(true);
    const handler = (e) => {
      setReducedMotion(e.matches);
      setIsPaused(e.matches);
    };
    setReducedMotion(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: getCategoryColor(event.category).primary,
      borderRadius: '6px',
      color: 'white',
      border: 'none',
      display: 'block',
      fontSize: '12px',
      padding: '2px 6px',
      fontWeight: '600',
    }
  });

  const availableCategories = useMemo(() => {
    const seen = new Set();
    return events.filter(e => {
      const key = normalizeKey(e.category);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map(e => e.category);
  }, [events]);

  const filteredEvents = useMemo(() => {
    const now = new Date();
    return events.flatMap(event => {
      if (selectedTiers.length > 0 && !selectedTiers.includes(event.provider?.tier)) return [];

      const rType = event.recurrence?.type || 'none';
      if (rType === 'none') {
        if (new Date(event.end) < now) return [];
        if (selectedCategory && normalizeKey(event.category) !== normalizeKey(selectedCategory)) return [];
        if (selectedDate && !isSameDay(event.start, selectedDate)) return [];
        return [event];
      }
      const next = nextOccurrence(event, now);
      if (!next) return [];
      if (selectedCategory && normalizeKey(event.category) !== normalizeKey(selectedCategory)) return [];
      if (selectedDate && !isSameDay(next.start, selectedDate)) return [];
      return [next];
    });
  }, [events, selectedCategory, selectedTiers, selectedDate]);

  const sortedFilteredEvents = useMemo(() => {
    const sorted = [...filteredEvents];
    if (sortBy === 'newest') {
      sorted.sort((a, b) => new Date(b.createdAt || b.start) - new Date(a.createdAt || a.start));
    } else if (sortBy === 'providerAsc') {
      sorted.sort((a, b) => (a.provider?.businessName || '').localeCompare(b.provider?.businessName || ''));
    } else {
      sorted.sort((a, b) => a.start - b.start);
    }
    return sorted;
  }, [filteredEvents, sortBy]);

  const visibleEvents = sortedFilteredEvents.slice(0, visibleCount);
  const hasMore = visibleCount < sortedFilteredEvents.length;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setVisibleCount(prev => prev + ITEMS_PER_PAGE);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, activeTab]);

  const expandedCalendarEvents = useMemo(() => {
    const filtered = events.filter(e => {
      if (selectedCategory && normalizeKey(e.category) !== normalizeKey(selectedCategory)) return false;
      if (selectedTiers.length > 0 && !selectedTiers.includes(e.provider?.tier)) return false;
      return true;
    });
    return filtered.flatMap(event => expandEvent(event, calendarRange.start, calendarRange.end));
  }, [events, calendarRange, selectedCategory, selectedTiers]);

  // Date strip: 14 days starting from today
  const dateStripDays = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      d.setHours(0, 0, 0, 0);
      const hasEvents = events.some(e => new Date(e.end) >= now && isSameDay(e.start, d));
      days.push({ date: d, hasEvents });
    }
    return days;
  }, [events]);

  const getDateLabel = (date, index) => {
    if (index === 0) return t('events.dateStrip.today');
    if (index === 1) return t('events.dateStrip.tomorrow');
    return date.toLocaleDateString(i18n.language, { weekday: 'short' });
  };

  // Section scrollers
  const weekendEvents = useMemo(() => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const satOffset = dayOfWeek === 0 ? 6 : (6 - dayOfWeek);
    const sat = new Date(now);
    sat.setDate(now.getDate() + satOffset);
    sat.setHours(0, 0, 0, 0);
    const sun = new Date(sat);
    sun.setDate(sat.getDate() + 1);
    sun.setHours(23, 59, 59, 999);
    return filteredEvents.filter(e => {
      const s = new Date(e.start);
      return s >= sat && s <= sun;
    }).sort((a, b) => a.start - b.start).slice(0, 10);
  }, [filteredEvents]);

  const justAnnouncedEvents = useMemo(() => {
    const now = new Date();
    const twoWeeksLater = new Date(now);
    twoWeeksLater.setDate(now.getDate() + 14);
    return filteredEvents
      .filter(e => {
        if (e.createdAt) {
          const created = new Date(e.createdAt);
          const sevenDaysAgo = new Date(now);
          sevenDaysAgo.setDate(now.getDate() - 7);
          return created >= sevenDaysAgo;
        }
        const s = new Date(e.start);
        return s >= now && s <= twoWeeksLater;
      })
      .sort((a, b) => new Date(b.createdAt || b.start) - new Date(a.createdAt || a.start))
      .slice(0, 10);
  }, [filteredEvents]);

  const handleOpenModal = (event) => {
    setSelectedEvent(event);
    setIsDescExpanded(false);
  };

  const handleToggleSave = async (e, eventId) => {
    e.stopPropagation();
    if (!user) {
      toast(t('events.event-management.log-in-prompt'), {
        icon: '🔒',
        duration: 4000,
      });
      return;
    }
    const id = eventId || selectedEvent?._id;
    if (!id) return;
    const isSaved = savedEventIds.includes(id);
    setSavedEventIds(prev => isSaved ? prev.filter(eid => eid !== id) : [...prev, id]);
    try {
      await api.put(`/user/event/${id}`);
    } catch (err) {
      setSavedEventIds(prev => isSaved ? [...prev, id] : prev.filter(eid => eid !== id));
    }
  };

  const clearAllFilters = () => {
    setSelectedCategory('');
    setSelectedTiers([]);
    setSelectedDate(null);
    setDescription('');
    setServiceProvider('');
  };

  const hasActiveFilters = selectedCategory || selectedTiers.length > 0 || selectedDate || description || serviceProvider;

  return (
    <div className="w-full relative font-sans text-gray-900">

      {/* Search & Filters — sticky, full-viewport-width, below Navbar */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-100 shadow-sm py-2.5" role="search" aria-label={t('events.search-button')}>
        <div className="w-full max-w-7xl mx-auto px-4">
        {/* Search row */}
        <div className="flex gap-2 mb-2">
          <div className="relative flex-1 min-w-0">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
            <input
              type="search"
              placeholder={t('events.event-management.search-by-description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchEvents(); }}
              aria-label={t('events.event-management.search-by-description')}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none text-sm bg-white min-h-[44px] transition-colors"
            />
          </div>
          <div className="relative flex-1 min-w-0 hidden sm:block">
            <BuildingStorefrontIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
            <input
              type="search"
              placeholder={t('events.event-management.search-by-service-provider')}
              value={serviceProvider}
              onChange={(e) => setServiceProvider(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchEvents(); }}
              aria-label={t('events.event-management.search-by-service-provider')}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none text-sm bg-white min-h-[44px] transition-colors"
            />
          </div>
          <button
            onClick={fetchEvents}
            aria-label={t('events.search-button')}
            className="bg-brand-primary text-white font-bold px-4 rounded-xl hover:bg-brand-primary-hover active:scale-95 transition-all text-sm min-h-[44px] min-w-[44px] flex items-center justify-center gap-2 flex-shrink-0"
          >
            <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
            <span className="hidden md:inline">{t('events.search-button')}</span>
          </button>
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              aria-label={t('events.clearFilters')}
              className="text-red-500 hover:text-red-600 active:scale-95 transition-all min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0 border border-red-200 rounded-xl bg-red-50"
            >
              <span className="text-xs font-bold">✕</span>
            </button>
          )}
        </div>
        <p className="text-[10px] text-gray-400 -mt-1 mb-1">{t('events.search-hint')}</p>

        {/* Mobile-only: provider search */}
        <div className="sm:hidden mb-2">
          <div className="relative">
            <BuildingStorefrontIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
            <input
              type="search"
              placeholder={t('events.event-management.search-by-service-provider')}
              value={serviceProvider}
              onChange={(e) => setServiceProvider(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchEvents(); }}
              aria-label={t('events.event-management.search-by-service-provider')}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none text-sm bg-white min-h-[44px] transition-colors"
            />
          </div>
        </div>

        {/* Category chips — scroll within the bar, no negative margins */}
        {availableCategories.length > 0 && (
          <div className="overflow-x-auto no-scrollbar snap-x snap-mandatory mb-1.5" role="group" aria-label="Categories">
            <div className="flex gap-1.5 w-max py-0.5">
              <button
                onClick={() => setSelectedCategory('')}
                className={`flex-shrink-0 snap-start px-3 py-1.5 rounded-full text-xs font-bold border transition-all min-h-[36px] active:scale-95 ${
                  !selectedCategory
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                {t('map.all')}
              </button>
              {availableCategories.map(cat => {
                const key = normalizeKey(cat);
                const isActive = normalizeKey(selectedCategory) === key;
                const { primary, light } = getCategoryColor(cat);
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(isActive ? '' : cat)}
                    className="flex-shrink-0 snap-start px-3 py-1.5 rounded-full text-xs font-bold border transition-all min-h-[36px] active:scale-95"
                    style={{
                      backgroundColor: isActive ? primary : light,
                      color: isActive ? 'white' : primary,
                      borderColor: primary,
                    }}
                  >
                    {t(`events.event-management.event-type.${key}`, { defaultValue: cat })}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Provider tier chips — scroll within the bar, no negative margins */}
        <div className="overflow-x-auto no-scrollbar snap-x snap-mandatory" role="group" aria-label="Provider tiers">
          <div className="flex gap-1.5 w-max py-0.5">
            <button
              onClick={() => setSelectedTiers([])}
              className="flex-shrink-0 snap-start flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition-all min-h-[32px] active:scale-95"
              style={{
                background: selectedTiers.length === 0 ? '#1d5cab' : 'white',
                color: selectedTiers.length === 0 ? 'white' : '#64748b',
                borderColor: selectedTiers.length === 0 ? '#1d5cab' : '#e2e8f0',
              }}
            >
              <Squares2X2Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {t('map.all')}
            </button>
            {Object.entries(TIER_META).map(([tier, meta]) => {
              const isActive = selectedTiers.includes(tier);
              const Icon = meta.icon;
              return (
                <button
                  key={tier}
                  onClick={() => setSelectedTiers(prev =>
                    prev.includes(tier) ? prev.filter(item => item !== tier) : [...prev, tier]
                  )}
                  className="flex-shrink-0 snap-start flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition-all min-h-[32px] active:scale-95"
                  style={{
                    background: isActive ? meta.color : 'white',
                    color: isActive ? 'white' : '#64748b',
                    borderColor: isActive ? meta.color : '#e2e8f0',
                  }}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {t(`map.${tier.toLowerCase()}`)}
                </button>
              );
            })}
          </div>
        </div>
        </div>{/* end max-w-7xl inner */}
      </div>

      {/* Main content */}
      <div className="w-full max-w-7xl mx-auto px-4 pb-4 md:pb-8">

      {/* Breadcrumb + Title */}
      <div className="pt-4 md:pt-6">
        <nav className="mb-3 flex items-center gap-1.5 text-xs text-gray-400">
          <Link to="/" className="hover:text-brand-primary transition-colors font-medium">{t('events.breadcrumb.home')}</Link>
          <span>/</span>
          <span className="text-gray-600 font-semibold">{t('events.pageTitle')}</span>
        </nav>
        <div className="mb-4">
          <h1 className="text-2xl md:text-4xl font-black tracking-tight text-gray-900">{t('events.pageTitle')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('events.subtitle')}</p>
        </div>
      </div>

      {/* Date strip */}
      <DateStrip
        days={dateStripDays}
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        isSameDay={isSameDay}
        getDateLabel={getDateLabel}
      />

      {/* Featured Carousel */}
      <FeaturedCarousel
        starEvents={starEvents}
        currentIndex={currentCarouselIndex}
        prevIndex={prevIndexRef.current}
        isAnimating={isCarouselAnimating}
        reducedMotion={reducedMotion}
        onNext={nextCarousel}
        onPrev={prevCarousel}
        onSetIndex={(i) => { prevIndexRef.current = currentCarouselIndex; setCurrentCarouselIndex(i); }}
        onPause={() => setIsPaused(true)}
        onResume={() => setIsPaused(false)}
        touchStartX={touchStartX}
        formatEventDate={formatEventDate}
        normalizeKey={normalizeKey}
        onOpenModal={handleOpenModal}
        t={t}
      />

      {/* Section scrollers */}
      {!isLoading && (
        <div className="mb-2">
          <SectionScroller title={t('events.sections.trending')} events={starEvents.slice(0, 10)} formatEventDate={formatEventDate} onOpenModal={handleOpenModal} />
          <SectionScroller title={t('events.sections.thisWeekend')} events={weekendEvents} formatEventDate={formatEventDate} onOpenModal={handleOpenModal} />
          <SectionScroller title={t('events.sections.justAnnounced')} events={justAnnouncedEvents} formatEventDate={formatEventDate} onOpenModal={handleOpenModal} />
        </div>
      )}

      {/* Divider */}
      <div className="h-px bg-gray-100 mb-6" />

      {/* Tabs */}
      <div className="flex mb-5 bg-gray-100 rounded-2xl p-1 gap-1 overflow-hidden" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'cards'}
          onClick={() => setActiveTab('cards')}
          className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 py-3 px-3 md:px-4 rounded-xl font-bold text-sm transition-all min-h-[48px] ${
            activeTab === 'cards'
              ? 'bg-white shadow-sm text-gray-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <ListBulletIcon className="h-5 w-5 flex-shrink-0" />
          <span className="text-xs sm:text-sm text-center">{t('events.upcoming-events')}</span>
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'calendar'}
          onClick={() => setActiveTab('calendar')}
          className={`flex-1 flex items-center justify-center gap-1.5 md:gap-2 py-3 px-3 md:px-4 rounded-xl font-bold text-sm transition-all min-h-[48px] ${
            activeTab === 'calendar'
              ? 'bg-white shadow-sm text-gray-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <CalendarIcon className="h-5 w-5 flex-shrink-0" />
          <span className="text-xs sm:text-sm text-center">{t('calendar.agenda') || 'Calendar'}</span>
        </button>
      </div>

      {/* Tab 1 — Event Cards */}
      {activeTab === 'cards' && (
        <div>
          <SortToolbar
            totalCount={sortedFilteredEvents.length}
            sortBy={sortBy}
            onSortChange={setSortBy}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearAllFilters}
            t={t}
          />

          {/* Loading skeleton */}
          {isLoading && (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="flex flex-col sm:flex-row">
                    <div className="sm:w-48 h-40 sm:h-auto min-h-[160px] animate-pulse bg-gray-100" />
                    <div className="p-5 flex-1 space-y-3">
                      <div className="animate-pulse bg-gray-100 rounded-lg h-4 w-20" />
                      <div className="animate-pulse bg-gray-100 rounded-lg h-5 w-3/4" />
                      <div className="animate-pulse bg-gray-100 rounded-lg h-4 w-1/2" />
                      <div className="animate-pulse bg-gray-100 rounded-lg h-4 w-1/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && sortedFilteredEvents.length === 0 && (
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12 text-center border border-gray-100">
              <CalendarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-700 mb-2">{t('events.noResultsTitle')}</h3>
              <p className="text-sm text-gray-500 mb-5">{t('events.noResultsBody')}</p>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="bg-[#1d5cab] text-white font-bold rounded-xl px-6 py-2.5 hover:bg-[#164a96] transition-colors text-sm min-h-[44px]"
                >
                  {t('events.clearFilters')}
                </button>
              )}
            </div>
          )}

          {/* Event cards */}
          {!isLoading && sortedFilteredEvents.length > 0 && (
            <>
              <div className="space-y-6">
                {visibleEvents.map((event, idx) => (
                  <EventCard
                    key={event._id}
                    event={event}
                    index={idx}
                    formatEventDate={formatEventDate}
                    normalizeKey={normalizeKey}
                    savedEventIds={savedEventIds}
                    onOpenModal={handleOpenModal}
                    onToggleSave={handleToggleSave}
                    t={t}
                    i18nLanguage={i18n.language}
                  />
                ))}
              </div>

              <div ref={sentinelRef} className="h-4 mt-2" />
              {hasMore && (
                <div className="text-center py-4" role="status" aria-live="polite">
                  <div className="inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Tab 2 — Calendar */}
      {activeTab === 'calendar' && (
        <CalendarTab
          events={expandedCalendarEvents}
          eventsLoaded={eventsLoaded}
          calendarHeight={calendarHeight}
          currentView={currentView}
          onViewChange={setCurrentView}
          onRangeChange={setCalendarRange}
          onOpenModal={handleOpenModal}
          eventStyleGetter={eventStyleGetter}
          currentLang={currentLang}
          t={t}
        />
      )}

      </div>{/* end main content */}

      {/* Modal */}
      <EventModal
        event={selectedEvent}
        formatEventDate={formatEventDate}
        normalizeKey={normalizeKey}
        savedEventIds={savedEventIds}
        isDescExpanded={isDescExpanded}
        onToggleDesc={() => setIsDescExpanded(!isDescExpanded)}
        onClose={() => setSelectedEvent(null)}
        onToggleSave={handleToggleSave}
        t={t}
        i18nLanguage={i18n.language}
      />

      {/* Back to top */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-40 p-3 bg-[#1d5cab] text-white rounded-full shadow-2xl hover:bg-[#164a96] active:scale-90 transition-all min-h-[48px] min-w-[48px] flex items-center justify-center safe-area-mb"
          aria-label={t('events.backToTop')}
        >
          <ArrowUpIcon className="h-5 w-5" />
        </button>
      )}

    </div>
  );
};

export default EventsPage;
